import { COLOR_GROUPS, GROUP_INFO, MAX_JAIL_ATTEMPTS, OWNABLE_FIELDS, BOARD } from "@grundbesitz/shared";
import type {
  ChatMessage,
  ColorGroup,
  Debt,
  EngineAction,
  PlayerAction,
  PlayerState,
  PublicGameState,
  RoomMember,
  RoomView,
} from "@grundbesitz/shared";
import { debtTotal, debtsOf, isHopeless, maxRaisable, mortgageInterest, netWorth, ownableAt, unmortgageCost, validateAction } from "@grundbesitz/engine";
import { fieldName, formatMoney } from "./format";

/** Derived, read-only views on the game state for the UI. Rules stay in the engine. */

export function selfPlayerOf(game: PublicGameState | null, selfId: string | null): PlayerState | null {
  if (!game || !selfId) return null;
  return game.players.find((p) => p.id === selfId) ?? null;
}

/** True when the viewer watches: spectator, not in the game, or eliminated. */
export function isSpectating(room: RoomView): boolean {
  if (room.you.spectator || room.you.playerId === null) return true;
  return selfPlayerOf(room.game, room.you.playerId)?.eliminated ?? true;
}

export function currentPlayerOf(game: PublicGameState): PlayerState {
  return game.players.find((p) => p.id === game.turn.playerId)!;
}

export function nameOf(game: PublicGameState | null, members: readonly RoomMember[], id: string): string {
  return game?.players.find((p) => p.id === id)?.name ?? members.find((m) => m.id === id)?.name ?? "Unbekannt";
}

// ---------------------------------------------------------------- action availability

export type Availability = { ok: true } | { ok: false; reason: string };

/** Asks the engine whether `action` would be accepted for the viewer right now. */
export function availability(game: PublicGameState, selfId: string | null, action: PlayerAction): Availability {
  if (!selfId) return { ok: false, reason: "Du schaust zu." };
  const error = validateAction(game, { ...action, player: selfId } as EngineAction);
  return error ? { ok: false, reason: error.message } : { ok: true };
}

// ---------------------------------------------------------------- phase specific views

export interface BuyOffer {
  field: number;
  fieldName: string;
  price: number;
  cash: number;
  /** How much cash is missing, 0 when affordable. */
  missing: number;
  /** Mortgages and building sales could cover the missing amount. */
  canRaise: boolean;
}

/** The pending purchase, when it is the viewer's decision. */
export function buyOfferFor(game: PublicGameState, selfId: string | null): BuyOffer | null {
  const pending = game.pending;
  if (game.phase !== "BUY_DECISION" || pending?.kind !== "BUY" || pending.playerId !== selfId) return null;
  const me = selfPlayerOf(game, selfId);
  const field = ownableAt(pending.field);
  if (!me || !field) return null;
  const missing = Math.max(0, field.price - me.cash);
  return {
    field: pending.field,
    fieldName: field.name,
    price: field.price,
    cash: me.cash,
    missing,
    canRaise: missing > 0 && maxRaisable(game, me.id) >= field.price,
  };
}

export interface DebtView {
  debts: Debt[];
  total: number;
  cash: number;
  /** Amount still missing (total - cash), never negative. */
  shortfall: number;
  maxRaisable: number;
  hopeless: boolean;
}

export function debtViewFor(game: PublicGameState, playerId: string): DebtView | null {
  const debts = debtsOf(game, playerId);
  if (debts.length === 0) return null;
  const me = game.players.find((p) => p.id === playerId);
  if (!me) return null;
  const total = debtTotal(game, playerId);
  return {
    debts,
    total,
    cash: me.cash,
    shortfall: Math.max(0, total - me.cash),
    maxRaisable: maxRaisable(game, playerId),
    hopeless: isHopeless(game, playerId),
  };
}

/** Distinct debtors in debt mode, in turn order. */
export function debtorsOf(game: PublicGameState): PlayerState[] {
  const ids = new Set(game.debts.map((d) => d.debtor));
  return game.players.filter((p) => ids.has(p.id));
}

export function mortgageDecisionsOf(game: PublicGameState, playerId: string | null) {
  return playerId ? game.mortgageDecisions.filter((d) => d.playerId === playerId) : [];
}

export function openTradesToMe(game: PublicGameState, selfId: string | null): number {
  return selfId ? game.trades.filter((t) => t.to === selfId).length : 0;
}

/** Failed jail attempts shown as "Versuch n von 3". */
export function jailAttemptLabel(player: PlayerState): string {
  return `Versuch ${Math.min(player.jailAttempts + 1, MAX_JAIL_ATTEMPTS)} von ${MAX_JAIL_ATTEMPTS}`;
}

// ---------------------------------------------------------------- status line

export interface StatusLine {
  /** Player the game currently waits for, null when several or nobody. */
  waitingFor: string[];
  text: string;
  /** The viewer has to act. */
  yourMove: boolean;
}

/** What is going on right now, from the viewer's perspective ("Anna würfelt ..."). */
export function statusLine(game: PublicGameState, members: readonly RoomMember[], selfId: string | null): StatusLine {
  const name = (id: string) => nameOf(game, members, id);
  const cur = currentPlayerOf(game);
  const mine = cur.id === selfId;
  const line = (text: string, waitingFor: string[], yourMove: boolean): StatusLine => ({ text, waitingFor, yourMove });

  switch (game.phase) {
    case "GAME_OVER":
      return line("Das Spiel ist beendet.", [], false);
    case "MORTGAGE_DECISION": {
      const d = game.mortgageDecisions[0];
      if (d.playerId === selfId) return line(`Entscheide über die Hypothek auf ${fieldName(d.field)}.`, [d.playerId], true);
      return line(`${name(d.playerId)} entscheidet über die Hypothek auf ${fieldName(d.field)}.`, [d.playerId], false);
    }
    case "DEBT": {
      const debtors = debtorsOf(game);
      const ids = debtors.map((p) => p.id);
      if (selfId && ids.includes(selfId)) return line("Du hast Schulden und musst Geld beschaffen.", ids, true);
      const names = debtors.map((p) => p.name).join(" und ");
      return line(`${names} ${debtors.length === 1 ? "muss" : "müssen"} Schulden begleichen.`, ids, false);
    }
    case "AUCTION":
      return line("Versteigerung läuft.", game.auction?.eligible ?? [], !!selfId && !!game.auction?.eligible.includes(selfId));
    case "BUY_DECISION": {
      const f = game.pending?.kind === "BUY" ? fieldName(game.pending.field) : "das Grundstück";
      return mine ? line("Du entscheidest über den Kauf.", [cur.id], true) : line(`${cur.name} entscheidet über den Kauf von ${f}.`, [cur.id], false);
    }
    case "CARD_CHOICE":
      return mine ? line("Du wählst: Strafe zahlen oder Ereigniskarte ziehen.", [cur.id], true) : line(`${cur.name} wählt zwischen Strafe und Ereigniskarte.`, [cur.id], false);
    case "IN_JAIL_DECISION":
      return mine
        ? line(`Du sitzt im Gefängnis (${jailAttemptLabel(cur)}).`, [cur.id], true)
        : line(`${cur.name} sitzt im Gefängnis und entscheidet.`, [cur.id], false);
    case "AWAIT_ROLL":
      return mine ? line("Du bist am Zug.", [cur.id], true) : line(`${cur.name} würfelt …`, [cur.id], false);
    case "AWAIT_END_TURN":
      return mine ? line("Du kannst bauen, handeln oder den Zug beenden.", [cur.id], true) : line(`${cur.name} beendet den Zug …`, [cur.id], false);
  }
}

// ---------------------------------------------------------------- players

export interface PlayerRow {
  player: PlayerState;
  member: RoomMember | undefined;
  isSelf: boolean;
  isCurrent: boolean;
  isBot: boolean;
  connected: boolean;
  autoPlay: boolean;
  inDebt: boolean;
  properties: number;
  buildings: number;
  netWorth: number;
}

/** One row per player in turn order. */
export function playerRows(room: RoomView): PlayerRow[] {
  const game = room.game;
  if (!game) return [];
  return game.players.map((player) => {
    const member = room.members.find((m) => m.id === player.id);
    let properties = 0;
    let buildings = 0;
    for (const i of OWNABLE_FIELDS) {
      const prop = game.properties[i];
      if (prop.owner === player.id) {
        properties++;
        buildings += prop.buildings;
      }
    }
    return {
      player,
      member,
      isSelf: player.id === room.you.playerId,
      isCurrent: player.id === game.turn.playerId && !player.eliminated,
      isBot: member?.isBot ?? false,
      connected: member?.connected ?? true,
      autoPlay: member?.autoPlay ?? false,
      inDebt: game.debts.some((d) => d.debtor === player.id),
      properties,
      buildings,
      netWorth: netWorth(game, player.id),
    };
  });
}

export interface OwnedGroup {
  key: string;
  label: string;
  /** Color group for streets, null for railroads and utilities. */
  group: ColorGroup | null;
  fields: number[];
}

/** A player's properties grouped by color group, then railroads and utilities, in board order. */
export function ownedGroups(game: PublicGameState, playerId: string): OwnedGroup[] {
  const groups: OwnedGroup[] = [];
  for (const g of COLOR_GROUPS as readonly ColorGroup[]) {
    const fields = GROUP_INFO[g].fields.filter((i) => game.properties[i].owner === playerId);
    if (fields.length > 0) groups.push({ key: g, label: GROUP_INFO[g].name, group: g, fields });
  }
  const kinds: [string, string, "RAILROAD" | "UTILITY"][] = [
    ["railroad", "Bahnhöfe", "RAILROAD"],
    ["utility", "Werke", "UTILITY"],
  ];
  for (const [key, label, kind] of kinds) {
    const fields = OWNABLE_FIELDS.filter((i) => BOARD[i].kind === kind && game.properties[i].owner === playerId);
    if (fields.length > 0) groups.push({ key, label, group: null, fields });
  }
  return groups;
}

// ---------------------------------------------------------------- chat

/** Chat messages from other players newer than `lastSeenId`. */
export function unreadChat(chat: readonly ChatMessage[], lastSeenId: number, selfId: string | null): number {
  return chat.filter((m) => m.kind === "chat" && m.id > lastSeenId && m.playerId !== selfId).length;
}

// ---------------------------------------------------------------- small texts

/** "Miete fordern: M 90 von Anna" */
export function rentClaimLabel(game: PublicGameState, members: readonly RoomMember[]): string | null {
  const c = game.rentClaim;
  return c ? `Miete fordern: ${formatMoney(c.amount)} von ${nameOf(game, members, c.payer)}` : null;
}

/** Cost to lift a mortgage next to the interest to keep it, for the decision dialog. */
export function mortgageDecisionCosts(field: number): { unmortgage: number; interest: number } {
  return { unmortgage: unmortgageCost(field), interest: mortgageInterest(field) };
}
